import {
	HeadContent,
	Scripts,
	type ErrorComponentProps,
	createRootRouteWithContext,
	retainSearchParams,
	stripSearchParams,
} from '@tanstack/react-router';
import * as v from 'valibot';
import appCssUrl from '../app.css?url';
import type { QueryClient } from '@tanstack/react-query';
import { TanStackDevtools } from '@tanstack/react-devtools';
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools';
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools';
import { FormDevtoolsPanel } from '@tanstack/react-form-devtools';
import type { PortalLocale } from '@worldcoo/monolith/portals';
import { I18nProvider } from '@lingui/react';
import { type I18n } from '@lingui/core';
import { loadLandingTranslation } from '@/lib/i18n';
import { lazy, Suspense, useEffect, useState } from 'react';
import { onToasterActivated, flushPendingToasts } from '@/lib/toast-signal';
import {
	getRuntimeAnalyticsCode,
	getRuntimeCdnUrl,
	getRuntimeImgproxyUrl,
	getRuntimeRecaptchaSiteKey,
	getRuntimeAdyenClientKey,
} from '@/lib/shared';
import { currentDocumentTrace } from '@/lib/ssr-trace';
import { DefaultCatchBoundary } from './-components/default-catch-boundary';
import { NotFound } from './-components/not-found';

const Toaster = lazy(() => import('@worldcoo/ui/components/sonner').then((m) => ({ default: m.Toaster })));

export interface MyRouterContext {
	queryClient: QueryClient;
	// Request-scoped Lingui instance (see createLocaleI18n). The SSR render tree reads its
	// translations from this instance via <I18nProvider>, so concurrent requests in the same
	// worker can't clobber each other's active locale.
	i18n: I18n;
	baseUrl: string;
	portalId: string;
	locale: PortalLocale;
	// Whether the URL being rendered carries an explicit locale segment (`/es/...`) versus the
	// unprefixed root (`/...`). SEO components use it to keep the canonical URL pointing at the page
	// itself rather than at the prefixed hreflang variant (Lighthouse and Google flag a canonical that
	// matches a non-self hreflang alternate as invalid). It lives in the context, never in module
	// state, so concurrent SSR renders can't read each other's URL shape; the server dehydrates it and
	// the client re-applies it on hydrate, so the first client render agrees with the markup it is
	// hydrating (TEC-3607).
	hasLocalePrefix: boolean;
	isBot: boolean;
	isInIframe: boolean;
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
	validateSearch: v.object({
		q: v.fallback(v.optional(v.string()), undefined),
		lang: v.fallback(
			v.optional(
				v.picklist([
					'ca',
					'ca-ES',
					'de',
					'de-DE',
					'en',
					'en-EN',
					'en-US',
					'en-GB',
					'es',
					'es-ES',
					'eu',
					'eu-ES',
					'fr',
					'fr-FR',
					'gl',
					'gl-ES',
					'it',
					'it-IT',
					'nl',
					'nl-NL',
					'pt',
					'pt-PT',
					'pt-BR',
				]),
			),
			undefined,
		),
		iframe: v.fallback(v.optional(v.literal(true)), undefined),
		city: v.fallback(v.optional(v.string()), undefined),
	}),
	search: {
		// TODO: retainSearchParams is not working correctly with default values and stripSearchParams,
		// so we're passing search={(prev) => prev} manually in the links for now
		middlewares: [stripSearchParams({ q: '' }), retainSearchParams(['lang', 'iframe', 'city'])],
	},
	// Reload translations when the locale from the URL (resolved by rewrite.input)
	// differs from the one loaded during router creation (which uses cookie/Accept-Language).
	// Reload the request-scoped instance (used by the SSR render tree) and only that one:
	// `loadLandingTranslation` activates the instance it is given, deliberately leaving the shared
	// module-global `i18n` alone on the server. See {@link createLocaleI18n}.
	beforeLoad: async ({ context }) => {
		if (context.i18n.locale !== context.locale) {
			await loadLandingTranslation(context.locale, context.i18n);
		}
	},
	head: () => {
		const cdnUrl = getRuntimeCdnUrl();
		const imgproxyUrl = getRuntimeImgproxyUrl();
		const analyticsCode = getRuntimeAnalyticsCode();
		const recaptchaSiteKey = getRuntimeRecaptchaSiteKey();
		const adyenClientKey = getRuntimeAdyenClientKey();
		// The trace this document was rendered in. Baking it into the HTML is what lets everything the
		// page does afterwards — its API calls, a crash it reports, the clicks leading up to that crash —
		// attach to the same trace as the render itself, instead of being four unrelated records.
		//
		// In the browser the ids are read back off `window`, so this script renders identically there:
		// every entry of `scripts` has to exist on both sides, or the head shifts by a position at
		// hydration and React throws the tree away. See {@link currentDocumentTrace}.
		//
		// It gets its OWN script, and must NOT be folded into `initAssetUrl`, whose content has to stay
		// byte-identical between server and client (see below). TanStack's `Script` dedups by comparing the
		// text it renders against the inline scripts already in the document, so any divergence in that text
		// makes the comparison fail and the whole bootstrap is re-injected and re-executed on every
		// hydration.
		const ssrTrace = currentDocumentTrace();
		const initTrace = ssrTrace
			? `window.__TRACE_ID=${JSON.stringify(ssrTrace.traceId)};` +
				`window.__ROOT_SPAN_ID=${JSON.stringify(ssrTrace.rootSpanId)};`
			: undefined;
		// Defines globalThis.__assetUrl for Vite's renderBuiltUrl runtime form
		// (see vite.config.ts) and stashes deployment env vars on window so the
		// rest of the app (router, image, recaptcha, adyen) can resolve them at
		// runtime instead of build time. Must execute before the deferred entry
		// module loads any chunk. The same values are also produced server-side
		// from process.env so SSR markup matches the client's first render.
		const initAssetUrl =
			`window.__CDN_URL=${JSON.stringify(cdnUrl)};` +
			`window.__IMGPROXY_URL=${JSON.stringify(imgproxyUrl)};` +
			`window.__ANALYTICS_CODE=${JSON.stringify(analyticsCode)};` +
			`window.__GOOGLE_RECAPTCHA_SITE_KEY=${JSON.stringify(recaptchaSiteKey)};` +
			`window.__ADYEN_CLIENT_KEY=${JSON.stringify(adyenClientKey)};` +
			`globalThis.__assetUrl=function(f){var b=window.__CDN_URL||'';return (b?b.replace(/\\/$/,'')+'/':'/')+f}`;
		return {
			meta: [
				{
					charSet: 'utf-8',
				},
				{
					name: 'viewport',
					content: 'width=device-width, initial-scale=1',
				},
			],
			links: analyticsCode
				? [{ rel: 'preconnect', href: 'https://www.googletagmanager.com', crossOrigin: 'anonymous' }]
				: [],
			scripts: [
				// Before the bootstrap, so the ids are set by the time any chunk runs.
				...(initTrace ? [{ children: initTrace }] : []),
				{ children: initAssetUrl },
				...(analyticsCode
					? [
							{
								src: `https://www.googletagmanager.com/gtag/js?id=${analyticsCode}`,
								defer: true,
							},
						]
					: []),
			],
		};
	},
	errorComponent: RootErrorComponent,
	notFoundComponent: RootNotFound,
	shellComponent: RootDocument,
});

// The error/not-found components read the request-scoped Lingui instance from route context
// rather than the shared global `i18n`. On the server the global can be flipped by a concurrent
// request mid-render, so rendering these through the global risks a locale mismatch between SSR
// and client hydration (React #418). See {@link createLocaleI18n}.
function RootErrorComponent(props: ErrorComponentProps) {
	const { i18n } = Route.useRouteContext();
	return (
		<I18nProvider i18n={i18n}>
			<DefaultCatchBoundary {...props} />
		</I18nProvider>
	);
}

function RootNotFound() {
	const { i18n } = Route.useRouteContext();
	return (
		<I18nProvider i18n={i18n}>
			<NotFound />
		</I18nProvider>
	);
}

function OnDemandToaster() {
	const [show, setShow] = useState(false);
	useEffect(() => onToasterActivated(() => setShow(true)), []);
	if (!show) return null;
	return (
		<Suspense>
			<ToasterWithFlush />
		</Suspense>
	);
}

function ToasterWithFlush() {
	useEffect(() => {
		flushPendingToasts();
	}, []);
	return (
		<Toaster
			className="whitespace-pre-line print:hidden"
			offset={{ top: 'calc(32px + env(safe-area-inset-top))' }}
			mobileOffset={{ top: 'calc(16px + env(safe-area-inset-top))' }}
			position="top-center"
			theme="light"
		/>
	);
}

function RootDocument({ children }: { children: React.ReactNode }) {
	const { locale } = Route.useRouteContext();
	const cdnUrl = import.meta.env.DEV ? '' : getRuntimeCdnUrl();
	const imgproxyUrl = getRuntimeImgproxyUrl();

	useEffect(() => {
		if (import.meta.env.DEV) {
			void import('react-grab');
		}
	}, []);

	return (
		<html lang={locale}>
			<head>
				<HeadContent />

				{cdnUrl && <link rel="preconnect" href={cdnUrl} crossOrigin="anonymous" />}
				{imgproxyUrl && imgproxyUrl !== cdnUrl && <link rel="preconnect" href={imgproxyUrl} crossOrigin="anonymous" />}
				<link rel="stylesheet" href={appCssUrl} />
				<link
					rel="preload"
					href={`${cdnUrl}/rethink-sans-variable.woff2`}
					as="font"
					type="font/woff2"
					crossOrigin="anonymous"
				/>
				<style>
					{'@font-face {' +
						"font-family: 'Rethink Sans Variable';" +
						'font-style: normal;' +
						'font-display: swap;' +
						'font-weight: 300 800;' +
						`src: url('${cdnUrl}/rethink-sans-variable.woff2') format('woff2');` +
						'}'}
				</style>
			</head>

			<body className="min-h-dvh overflow-y-scroll">
				{children}
				<OnDemandToaster />

				<div className="hidden sm:block">
					<TanStackDevtools
						plugins={[
							{
								name: 'Tanstack Router',
								render: <TanStackRouterDevtoolsPanel />,
							},
							{
								name: 'Tanstack Query',
								render: <ReactQueryDevtoolsPanel />,
							},
							{
								name: 'Tanstack Form',
								render: <FormDevtoolsPanel />,
							},
						]}
					/>
				</div>
				<Scripts />
			</body>
		</html>
	);
}
